import React, { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Checkbox } from "@/components/ui/checkbox"
import { ChevronDown, RefreshCw } from "lucide-react"
import { cn } from "@/lib/utils"
import { useDebounce } from "@/hooks/useDebounce"
import { TimeRange, OvertimeStatus } from "@/types/enums"

export interface DashboardFiltersProps {
  onFiltersChange: (filters: DashboardFilters) => void
  departments?: string[]
  isLoading?: boolean
  className?: string
}

export interface DashboardFilters {
  departments: string[]  // Changed to array for multi-select
  statuses: string[]     // Changed to array for multi-select
  timeRange: string
}

const statusOptions = [
  { value: "all", label: "All Statuses" },
  { value: OvertimeStatus.APPROVED, label: "Approved" },
  { value: OvertimeStatus.REJECTED, label: "Rejected" },
  { value: OvertimeStatus.PENDING, label: "Pending" },
]

// Time range configuration - maps enum values to display labels
const timeRangeOptions = [
  { value: TimeRange.CALENDAR_MONTH, label: "Calendar Month" },
  { value: TimeRange.LAST_CALENDAR_MONTH, label: "Last Calendar Month" },
  { value: TimeRange.SEVEN_DAYS, label: "Last 7 days" },
  { value: TimeRange.THIRTY_DAYS, label: "Last 30 days" },
  { value: TimeRange.NINETY_DAYS, label: "Last 3 months" },
  { value: TimeRange.THREE_SIXTY_FIVE_DAYS, label: "Last year" },
  { value: TimeRange.ALL, label: "All time" },
]

export default function DashboardFilters({ 
  onFiltersChange, 
  departments = [], 
  isLoading = false,
  className 
}: DashboardFiltersProps) {
  // Single state for filters that apply immediately
  const [filters, setFilters] = useState<DashboardFilters>({
    timeRange: TimeRange.THIRTY_DAYS,
    departments: [],
    statuses: []
  })

  // Debounce the filters to reduce excessive re-renders
  const debouncedFilters = useDebounce(filters, 300)

  // Notify parent of filter changes with debouncing
  useEffect(() => {
    onFiltersChange(debouncedFilters)
  }, [debouncedFilters, onFiltersChange])

  const handleTimeRangeChange = (timeRange: string) => {
    setFilters(prev => ({ ...prev, timeRange }))
  }

  const handleDepartmentToggle = (department: string) => {
    setFilters(prev => {
      if (department === "all") {
        // If "All" is selected, clear other selections
        return { ...prev, departments: [] }
      } else {
        // If a specific department is selected, remove "all" and toggle the department
        const newDepartments = prev.departments.includes(department)
          ? prev.departments.filter(d => d !== department)
          : [...prev.departments.filter(d => d !== "all"), department]
        return { ...prev, departments: newDepartments }
      }
    })
  }

  const handleStatusToggle = (status: string) => {
    setFilters(prev => {
      if (status === "all") {
        // If "All" is selected, clear other selections
        return { ...prev, statuses: [] }
      } else {
        // If a specific status is selected, remove "all" and toggle the status
        const newStatuses = prev.statuses.includes(status)
          ? prev.statuses.filter(s => s !== status)
          : [...prev.statuses.filter(s => s !== "all"), status]
        return { ...prev, statuses: newStatuses }
      }
    })
  }

  const handleReset = () => {
    setFilters({
      timeRange: TimeRange.THIRTY_DAYS,
      departments: [],
      statuses: []
    })
  }

  const hasActiveFilters = () => {
    return (
      filters.departments.length > 0 || 
      filters.statuses.length > 0 ||
      filters.timeRange !== TimeRange.THIRTY_DAYS
    )
  }

  const getDepartmentDisplayValue = () => {
    if (filters.departments.length === 0) return "All Departments"
    if (filters.departments.length === 1) return filters.departments[0]
    return `${filters.departments.length} selected`
  }

  const getStatusDisplayValue = () => {
    if (filters.statuses.length === 0) return "All Statuses"
    if (filters.statuses.length === 1) return statusOptions.find(s => s.value === filters.statuses[0])?.label || filters.statuses[0]
    return `${filters.statuses.length} selected`
  }

  // Create department options with "All" at the top
  const departmentOptions = [
    { value: "all", label: "All Departments" },
    ...departments.map(dept => ({ value: dept, label: dept }))
  ]

  return (
    <Card className={cn("shadow-sm border-border/50", className)}>
      <CardContent className="p-6">
        <div className="flex flex-col space-y-6 lg:flex-row lg:space-y-0 lg:space-x-8 lg:items-end">
          {/* Time Range Filter */}
          <div className="flex flex-col space-y-2 min-w-0 lg:min-w-[200px]">
            <label className="text-sm font-medium text-foreground">Time Range</label>
            <Select
              value={filters.timeRange}
              onValueChange={handleTimeRangeChange}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                {timeRangeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Department Filter - Multi-select */}
          <div className="flex flex-col space-y-2 min-w-0 lg:min-w-[220px]">
            <label className="text-sm font-medium text-foreground">Department</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="justify-between"
                  disabled={isLoading}
                >
                  {getDepartmentDisplayValue()}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-0">
                <Command>
                  <CommandInput placeholder="Search departments..." />
                  <CommandList>
                    <CommandEmpty>No departments found.</CommandEmpty>
                    <CommandGroup>
                      {departmentOptions.map((department) => (
                        <CommandItem
                          key={department.value}
                          onSelect={() => handleDepartmentToggle(department.value)}
                        >
                          <Checkbox
                            checked={department.value === "all" 
                              ? filters.departments.length === 0 
                              : filters.departments.includes(department.value)}
                            className="mr-2"
                          />
                          {department.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Status Filter - Multi-select */}
          <div className="flex flex-col space-y-2 min-w-0 lg:min-w-[180px]">
            <label className="text-sm font-medium text-foreground">Status</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="justify-between"
                  disabled={isLoading}
                >
                  {getStatusDisplayValue()}
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[180px] p-0">
                <Command>
                  <CommandList>
                    <CommandGroup>
                      {statusOptions.map((status) => (
                        <CommandItem
                          key={status.value}
                          onSelect={() => handleStatusToggle(status.value)}
                        >
                          <Checkbox
                            checked={status.value === "all" 
                              ? filters.statuses.length === 0 
                              : filters.statuses.includes(status.value)}
                            className="mr-2"
                          />
                          {status.label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Reset Button */}
          <div className="flex items-center lg:ml-auto">
            {hasActiveFilters() && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                disabled={isLoading}
                className="flex items-center space-x-1"
              >
                <RefreshCw className={cn("h-3 w-3", { "animate-spin": isLoading })} />
                <span>Reset</span>
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
} 